import { type Game } from '@/models/game';
import {
  Permissions,
  WILDCARD,
  grantCovers,
  hasPermission,
  holdsAll,
  type MyRoleGrant,
} from '@/models/permission';

import { replacedGrants } from './grantability';
import { grantKey, isAllSandboxes } from './member';
import { type GrantScope } from '../api/useRoleGrants';

/**
 * Whether this Grant carries `users:manage_permissions` — the viewer may never
 * revoke one of their own (#419 AC 9), whether or not it is their last.
 *
 * Which Grants count as permission-management is derived from the permissions they
 * carry, never from the Role name (AC 11); the name only ever reaches the copy.
 * admin-api enforces the same rule and stays authoritative
 * (srvc/permission/grant_service.go, guardSelfLockout).
 */
export const isManagementGrant = (grant: MyRoleGrant) =>
  grantCovers(grant, Permissions.users.managePermissions);

const FULL_WILDCARD = { resource: WILDCARD, action: WILDCARD };

/**
 * Whether the viewer may remove `grant` as far as BR-RBAC-70 goes: a Grant
 * conferring `*:*` — the administrator Role today, recognised by its permissions
 * and never by its name — only yields to a viewer holding `*:*` on a scope
 * containing its own. Any other Grant is unaffected. admin-api enforces the same
 * rule and stays authoritative.
 */
export const canRemoveAdministratorGrant = (
  viewer: MyRoleGrant[],
  grant: MyRoleGrant,
) =>
  !grant.permissions.some(
    ({ resource, action }) => resource === WILDCARD && action === WILDCARD,
  ) ||
  holdsAll(
    viewer,
    [FULL_WILDCARD],
    grant.game_code,
    grant.sandbox_code,
    grant.studio_code,
  );

/**
 * Why the viewer may not move this member between states, empty when they may —
 * on top of administering the whole Studio, which gates the buttons themselves.
 * The rules are admin-api's (noop#363), mirrored to hide a move it would refuse:
 * no one deactivates themselves, and a member holding an administrator Grant is
 * only moved by an Administrator on a scope containing it (BR-RBAC-71).
 */
export const membershipBlocker = (
  viewer: MyRoleGrant[],
  viewerId: number | undefined,
  member: { id: number; roles: MyRoleGrant[] },
  move: 'deactivate' | 'reactivate',
) => {
  if (move === 'deactivate' && viewerId === member.id)
    return 'You cannot deactivate yourself.';

  return member.roles.every((grant) =>
    canRemoveAdministratorGrant(viewer, grant),
  )
    ? ''
    : 'This member holds an administrator Grant. Only an Administrator on a scope containing it can change their state.';
};

/**
 * The single mechanism for every "not yours to remove" case: an empty string
 * means the Grant can be removed, anything else is the explanation shown instead
 * of removing it.
 */
export const removalReason = (myGrants: MyRoleGrant[], isSelf: boolean) => {
  return (grant: MyRoleGrant) => {
    const inScope = hasPermission(
      myGrants,
      Permissions.users.managePermissions,
      grant.game_code,
      grant.sandbox_code,
      grant.studio_code,
    );

    if (inScope) {
      // #419 AC 9. admin-api refuses this too, so the UI is the explanation, not
      // the rule.
      if (isSelf && isManagementGrant(grant)) {
        return `The ${grant.name} Grant carries permission management. You cannot revoke your own.`;
      }

      // BR-RBAC-70: managing permissions on the scope is not enough here.
      // Any other Grant is bounded by the scope alone (BR-RBAC-60): holding the
      // Role it confers is not required to remove it.
      return canRemoveAdministratorGrant(myGrants, grant)
        ? ''
        : `The ${grant.name} Grant confers every permission. Only an Administrator on this scope can remove it.`;
    }

    if (grant.studio_code === WILDCARD) {
      return 'This Grant covers every Game. Only a Nacon-wide Permission Manager can remove it.';
    }

    if (grant.game_code === WILDCARD) {
      return `This Grant covers every Game of ${grant.studio_code}. Only a Permission Manager of the whole Studio can remove it.`;
    }

    return `This Grant is on ${grant.game_code}${
      grant.sandbox_code === WILDCARD ? '' : ` · ${grant.sandbox_code}`
    }, outside the scope you administer.`;
  };
};

// What the sheet is asking to confirm. 'blocked' is the odd one out: it confirms
// nothing, it explains why the removal the viewer just asked for is unavailable.
//
// One row or twelve is the same shape: the per-row Remove button asks about a list
// of one, so there is one removal path rather than two that can drift.
export type Confirmation =
  | { kind: 'removeGrants'; grants: MyRoleGrant[] }
  | { kind: 'blocked'; reason: string }
  // The batch asked, and the member's narrower Grants it replaces (noop#359).
  // `replaced` is empty when only admin-api knew: a 409 on a stale read.
  | { kind: 'replaceGrants'; grants: GrantScope[]; replaced: MyRoleGrant[] };

/** The first Grant asked that is not removable blocks the whole batch. */
export const confirmationFor = (
  asked: MyRoleGrant[],
  reason: (grant: MyRoleGrant) => string,
): Confirmation => {
  const blocked = asked.map(reason).find((why) => why !== '');
  return blocked === undefined
    ? { kind: 'removeGrants', grants: asked }
    : { kind: 'blocked', reason: blocked };
};

/**
 * The replacement to confirm before granting `asked`, or null when it replaces
 * nothing and can go straight out.
 */
export const replacementFor = (
  asked: GrantScope[],
  member: MyRoleGrant[],
): Confirmation | null => {
  // Two Grants of the batch can replace the same narrower one: listed once.
  const replaced = [
    ...new Map(
      asked
        .flatMap((scope) => replacedGrants(member, scope.role_id, scope))
        .map((grant) => [grantKey(grant), grant]),
    ).values(),
  ];
  return replaced.length > 0
    ? { kind: 'replaceGrants', grants: asked, replaced }
    : null;
};

/** One Game's worth of the viewer's Grants — a tab. */
export interface GameGroup {
  gameCode: string;
  label: string;
  grants: MyRoleGrant[];
}

/**
 * The viewer's Grants bucketed per Game, broadest first, then by the name actually
 * shown so the order matches the labels rather than the codes behind them.
 *
 * The label falls back to the raw game_code: /v1/games may be slow, may fail, or may
 * omit a Game the viewer holds a Grant on, and none of that should blank a tab.
 */
export const groupByGame = (
  grants: MyRoleGrant[],
  games: Game[],
): GameGroup[] => {
  const buckets = new Map<string, MyRoleGrant[]>();
  for (const grant of grants) {
    buckets.set(grant.game_code, [
      ...(buckets.get(grant.game_code) ?? []),
      grant,
    ]);
  }

  return [...buckets]
    .map(([gameCode, gameGrants]) => ({
      gameCode,
      label:
        gameCode === WILDCARD
          ? 'All Games'
          : (games.find((game) => game.game_code === gameCode)?.name ??
            gameCode),
      grants: gameGrants,
    }))
    .sort((a, b) => {
      if (a.gameCode === WILDCARD) return -1;
      if (b.gameCode === WILDCARD) return 1;
      return a.label.localeCompare(b.label);
    });
};

// Broadest first, then alphabetical: 'All Sandboxes' is the widest scope a Grant can
// carry, so it heads the list instead of sorting under 'A'.
export const byScope = (a: MyRoleGrant, b: MyRoleGrant) => {
  if (isAllSandboxes(a) !== isAllSandboxes(b))
    return isAllSandboxes(a) ? -1 : 1;
  return a.sandbox_code.localeCompare(b.sandbox_code);
};

/**
 * One row per Role, holding every Sandbox it is granted on.
 *
 * A Role is defined once in the catalogue, so two Grants naming it carry the same
 * description and the same permissions: a row per Grant repeated the tallest cell of
 * the table once per Sandbox. `grant` is the Role's identity — any Grant of the group
 * answers for it — and `scopes` is what actually differs.
 *
 * game_code stays out of the key: it is already constant within a tab.
 */
export const groupByRole = (grants: MyRoleGrant[]) => {
  const buckets = new Map<
    string,
    { grant: MyRoleGrant; scopes: MyRoleGrant[] }
  >();
  for (const grant of grants) {
    const bucket = buckets.get(grant.id);
    if (bucket) bucket.scopes.push(grant);
    else buckets.set(grant.id, { grant, scopes: [grant] });
  }

  // Insertion order is the API's order — nothing here invents a ranking of Roles.
  return [...buckets.values()].map(({ grant, scopes }) => ({
    grant,
    scopes: [...scopes].sort(byScope),
  }));
};
